import type { AlertRuleSpec, AlertRuleView } from "@screener/protocol";
import { LockedBanner } from "@screener/uikit/components/locked-banner";
import { useState } from "react";
import type { AlertActionKey } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import { RuleFormSheet } from "./rule-form-sheet";
import {
  countRulesUsingTelegram,
  RuleList,
  RuleStatusBar,
  TelegramWarningBanner,
} from "./rule-list";
import type { AlertRuleWriteOutcome } from "./use-alert-rules";
import { useAlertRules } from "./use-alert-rules";

/**
 * Экран «Алерты» (spec `web-alerts-screen`, design.md stage6-alert-web,
 * tasks.md 6.4; 1.103, §116): таблица правил на всю ширину, тулбар сверху и
 * строка статуса снизу. Блок Telegram с экрана ушёл в
 * окно из меню пользователя (stage8-sidebar-layout, решение 3); статус бота
 * приходит пропсом из каркаса. Макет `AlertsScreen.dc.html` только как
 * ориентир, вёрстка компонентами `@screener/uikit`.
 */

/** Как исход удаления (ревью, пункт A.3) меняет локальное состояние экрана. */
export interface DeleteOutcomeEffect {
  /** Строка подтверждения закрывается — правило удалено или его уже нет. */
  closeConfirm: boolean;
  /** Текст отказа, остающийся в строке подтверждения; `null` — отказа нет. */
  deleteError: "unavailable" | "invalid" | null;
  /** Сообщение над списком; `null` — сообщения нет. */
  notice: string | null;
}

/**
 * Чистая часть обработки исхода удаления, тестируемая без React (тот же
 * приём, что `nextOpenChangeShouldClose`/`isCurrentAttempt` в
 * `rule-form-sheet.tsx`): `ok` и `not_found` закрывают строку подтверждения
 * (контроллер уже перечитал список — при `not_found` строки в нём больше
 * нет), `unavailable`/`invalid` оставляют её открытой с текстом отказа.
 * `unauthorized` уже обработан контроллером (`onSessionInvalid()`), `busy` —
 * защитный случай, до которого UI не доводит: оба просто возвращают строку
 * в обычный режим без текста.
 */
export function deleteOutcomeEffect(status: AlertRuleWriteOutcome["status"]): DeleteOutcomeEffect {
  if (status === "ok") {
    return { closeConfirm: true, deleteError: null, notice: null };
  }
  if (status === "not_found") {
    return {
      closeConfirm: true,
      deleteError: null,
      notice: "Правило уже удалено в другой вкладке.",
    };
  }
  if (status === "unavailable" || status === "invalid") {
    return { closeConfirm: false, deleteError: status, notice: null };
  }
  return { closeConfirm: false, deleteError: null, notice: null };
}

/** Как исход переключения действия из строки (§116 п. 2) меняет извещение над списком. */
export interface ToggleOutcomeEffect {
  /** Сообщение над списком; `null` — сообщения нет. */
  notice: string | null;
}

/**
 * Чистая часть обработки исхода переключения (design.md Д1), тестируемая без
 * React. Строка показывает значение из прочитанного правила, а не
 * оптимистичное, поэтому при отказе откатывать нечего — остаётся только
 * сказать, что действие не переключено. `not_found`: список уже перечитан
 * контроллером. `unauthorized` уже обработан им же (`onSessionInvalid()`).
 */
export function toggleOutcomeEffect(status: AlertRuleWriteOutcome["status"]): ToggleOutcomeEffect {
  switch (status) {
    case "ok":
    case "unauthorized":
      return { notice: null };
    case "not_found":
      return { notice: "Правило уже удалено в другой вкладке." };
    case "unavailable":
      return { notice: "Демон недоступен — действие не переключено." };
    case "invalid":
      return { notice: "Демон отклонил запись — действие не переключено." };
    case "busy":
      return { notice: "Запись уже идёт — действие не переключено." };
    default:
      return status satisfies never;
  }
}

/** Правило целиком с переключённым действием — тело той же записи `PUT`, что и у формы. */
export function toggledSpec(spec: AlertRuleSpec, action: AlertActionKey): AlertRuleSpec {
  return { ...spec, [action]: !spec[action] };
}

export interface AlertsScreenProps {
  /** В клиенте есть действительная сессия — правила читаются и пишутся. */
  enabled: boolean;
  /** Экран виден (каркас не скрыл его атрибутом `hidden`). */
  active: boolean;
  /** Биржи потока §7.6 — варианты биржи формы (design.md решение 4). */
  exchanges: readonly string[];
  /**
   * Бот Telegram настроен; `null` — статус ещё не получен: предупреждающий
   * вид строк и баннер не показываются, пока он не известен. Приходит из
   * каркаса, который держит настройки Telegram (design.md решение 3).
   */
  telegramConfigured: boolean | null;
  onSessionInvalid: () => void;
  /** Перечитывает карту действий вкладки §8 после успешной записи правила (design.md решение 7). */
  onRulesChanged: () => void;
}

type FormState = { mode: "create" } | { mode: "edit"; rule: AlertRuleView };

export function AlertsScreen({
  enabled,
  active,
  exchanges,
  telegramConfigured,
  onSessionInvalid,
  onRulesChanged,
}: AlertsScreenProps) {
  const rules = useAlertRules({ enabled, active, onSessionInvalid, onRulesChanged });
  const planGate = usePlanGate();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState | null>(null);
  // Исход удаления виден пользователю (ревью, пункт A.3): пока идёт запрос,
  // строка подтверждения остаётся открытой и показывает «Удаляем…»; отказ —
  // текст в той же строке; `not_found` — список уже перечитан контроллером,
  // здесь только сообщение над списком, которое живёт до следующего
  // действия со списком.
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<"unavailable" | "invalid" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const ruleList = rules.state.status === "ready" ? rules.state.rules : [];
  const telegramSenderCount = countRulesUsingTelegram(ruleList);

  function clearDeleteNotices(): void {
    setDeleteError(null);
    setNotice(null);
  }

  async function handleConfirmDelete(id: string): Promise<void> {
    setDeleting(true);
    setDeleteError(null);
    const outcome = await rules.remove(id);
    setDeleting(false);
    const effect = deleteOutcomeEffect(outcome.status);
    if (effect.closeConfirm) {
      setConfirmingId(null);
    }
    setDeleteError(effect.deleteError);
    setNotice(effect.notice);
  }

  async function handleToggleAction(view: AlertRuleView, action: AlertActionKey): Promise<void> {
    clearDeleteNotices();
    const outcome = await rules.replace(view.id, toggledSpec(view.rule, action));
    setNotice(toggleOutcomeEffect(outcome.status).notice);
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Тариф без звука и уведомлений: правила видны, но не срабатывают
          (§144 п. 8а) — замок полосой над списком, а не вместо него. */}
      {planGate.alertsLocked ? (
        <LockedBanner label="Звук и уведомления" text={planGate.soundText} />
      ) : null}
      <RuleList
        status={rules.state.status}
        rules={ruleList}
        telegramConfigured={telegramConfigured}
        confirmingId={confirmingId}
        writing={rules.writing}
        deleting={deleting}
        deleteError={deleteError}
        notice={notice}
        banner={
          telegramConfigured === false && telegramSenderCount > 0 ? (
            <div className="border-b border-border px-3 py-2">
              <TelegramWarningBanner count={telegramSenderCount} />
            </div>
          ) : null
        }
        onNewRule={() => {
          clearDeleteNotices();
          setConfirmingId(null);
          setFormState({ mode: "create" });
        }}
        onEdit={(rule) => {
          clearDeleteNotices();
          setConfirmingId(null);
          setFormState({ mode: "edit", rule });
        }}
        onAskDelete={(id) => {
          clearDeleteNotices();
          setConfirmingId(id);
        }}
        onCancelDelete={() => {
          clearDeleteNotices();
          setConfirmingId(null);
        }}
        onConfirmDelete={(id) => {
          void handleConfirmDelete(id);
        }}
        onToggleAction={(rule, action) => {
          void handleToggleAction(rule, action);
        }}
        onRetry={() => {
          clearDeleteNotices();
          rules.reload();
        }}
      />

      <RuleStatusBar count={rules.state.status === "ready" ? ruleList.length : null} />

      <RuleFormSheet
        open={formState !== null}
        mode={formState?.mode ?? "create"}
        initialRule={formState?.mode === "edit" ? formState.rule : null}
        exchanges={exchanges}
        telegramConfigured={telegramConfigured}
        writing={rules.writing}
        onClose={() => setFormState(null)}
        onSubmit={(spec) =>
          formState?.mode === "edit" ? rules.replace(formState.rule.id, spec) : rules.create(spec)
        }
      />
    </div>
  );
}
